import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IBlogQueryParams } from '../interfaces/types';
import { defaultFilterState, filterStateFromUrl, filterStateToUrl, IBlogFilterState } from '../data/url-state';

export function useBlogFilters(pageSize: number) {
    const [state, setState] = useState<IBlogFilterState>(() => filterStateFromUrl(window.location.search));

    // History writes happen in an effect after the state commit so setters can use
    // functional updates (batch-safe) without side effects inside the updater
    // (StrictMode double-invokes updaters). false = no write (initial mount, popstate).
    const historyWritePending = useRef(false);

    const applyState = useCallback((updater: (prev: IBlogFilterState) => IBlogFilterState) => {
        historyWritePending.current = true;
        setState(updater);
    }, []);

    useEffect(() => {
        if (!historyWritePending.current) return;
        window.history.pushState(null, '', filterStateToUrl(state));
        historyWritePending.current = false;
    }, [state]);

    useEffect(() => {
        const onPopState = () => setState(filterStateFromUrl(window.location.search));
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    // Backend endpoint still accepts arrays; single-select just sends 0 or 1 values.
    const queryParams: IBlogQueryParams = useMemo(() => ({
        searchText: state.searchText,
        categories: state.category ? [state.category] : [],
        publishDates: state.publishDate ? [state.publishDate] : [],
        pageNumber: state.page + 1,
        pageSize,
    }), [state.searchText, state.category, state.publishDate, state.page, pageSize]);

    // Search is submit-based: typing changes nothing until the user submits the form.
    const submitSearch = (searchText: string) =>
        applyState(prev => ({ ...prev, searchText: searchText.trim(), page: 0 }));

    const setCategory = (category: string) =>
        applyState(prev => ({ ...prev, category, page: 0 }));

    const setPublishDate = (publishDate: string) =>
        applyState(prev => ({ ...prev, publishDate, page: 0 }));

    const setPage = (page: number) =>
        applyState(prev => ({ ...prev, page }));

    const resetFilters = () =>
        applyState(() => ({ ...defaultFilterState }));

    return { state, queryParams, submitSearch, setCategory, setPublishDate, setPage, resetFilters };
}
